import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User, Mail, Phone, MapPin, Camera, Edit, Save, X, ArrowLeft,
  Calendar as CalendarIcon, Palmtree, FileText, PoundSterling,
  Clock, AlertCircle, CheckCircle, Upload, Download, Briefcase,
  Home, UserCircle, Shield, Heart, RefreshCw, Lock, Eye, EyeOff
} from "lucide-react";
import { API } from "@/App";

export const StaffProfile = () => {
  const navigate = useNavigate();
  const { userId } = useParams(); // For viewing other staff profiles
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const isViewingOther = userId && userId !== currentUser.user_id;
  const isDirector = currentUser.role === "director" || currentUser.role === "admin";
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [profile, setProfile] = useState(null);
  const [editedProfile, setEditedProfile] = useState({});
  
  // Holiday request modal
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayRequest, setHolidayRequest] = useState({
    start_date: null,
    end_date: null,
    notes: ""
  });
  const [submittingHoliday, setSubmittingHoliday] = useState(false);
  
  // Payslips
  const [payslips, setPayslips] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [syncingXero, setSyncingXero] = useState(false);
  
  // Change password
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Add payslip modal (directors only)
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [newPayslip, setNewPayslip] = useState({
    month: "",
    month_num: 1,
    year: new Date().getFullYear(),
    basic_pay: 0,
    commission: 0,
    bonus: 0,
    deductions: 0,
    gross_amount: 0,
    net_amount: 0,
    notes: ""
  });
  const [addingPayslip, setAddingPayslip] = useState(false);
  
  // Edit salary modal (directors only)
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [salaryData, setSalaryData] = useState({ basic_salary: 0, holiday_allowance: 28 });

  useEffect(() => {
    fetchProfile();
    if (!isViewingOther) {
      fetchPayslips();
      fetchHolidays();
    } else if (isDirector) {
      // Directors can view payslips for other staff
      fetchStaffPayslips();
    }
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const endpoint = isViewingOther 
        ? `${API}/staff/profile/${userId}`
        : `${API}/staff/profile/me`;
      const res = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data);
      setEditedProfile(res.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchPayslips = async () => {
    try {
      const res = await axios.get(`${API}/staff/payslips/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayslips(res.data.payslips || []);
    } catch (error) {
      console.error("Error fetching payslips:", error);
    }
  };

  const fetchStaffPayslips = async () => {
    try {
      const res = await axios.get(`${API}/staff/payslips/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayslips(res.data.payslips || []);
    } catch (error) {
      console.error("Error fetching staff payslips:", error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${API}/staff/holidays/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(res.data.holidays || []);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const handleAddPayslip = async () => {
    if (!newPayslip.month || !newPayslip.year) {
      toast.error("Please select month and year");
      return;
    }

    setAddingPayslip(true);
    try {
      const gross = parseFloat(newPayslip.basic_pay) + parseFloat(newPayslip.commission) + parseFloat(newPayslip.bonus);
      const net = gross - parseFloat(newPayslip.deductions);
      
      await axios.post(`${API}/staff/payslips/add`, {
        user_id: userId,
        month: newPayslip.month,
        month_num: newPayslip.month_num,
        year: parseInt(newPayslip.year),
        basic_pay: parseFloat(newPayslip.basic_pay) || 0,
        commission: parseFloat(newPayslip.commission) || 0,
        bonus: parseFloat(newPayslip.bonus) || 0,
        deductions: parseFloat(newPayslip.deductions) || 0,
        gross_amount: gross,
        net_amount: net,
        notes: newPayslip.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Payslip added successfully");
      setShowPayslipModal(false);
      setNewPayslip({
        month: "",
        month_num: 1,
        year: new Date().getFullYear(),
        basic_pay: 0,
        commission: 0,
        bonus: 0,
        deductions: 0,
        gross_amount: 0,
        net_amount: 0,
        notes: ""
      });
      fetchStaffPayslips();
    } catch (error) {
      console.error("Error adding payslip:", error);
      toast.error("Failed to add payslip");
    } finally {
      setAddingPayslip(false);
    }
  };

  const handleDeletePayslip = async (payslipId) => {
    if (!confirm("Are you sure you want to delete this payslip?")) return;
    
    try {
      await axios.delete(`${API}/staff/payslips/${payslipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payslip deleted");
      if (isViewingOther) {
        fetchStaffPayslips();
      } else {
        fetchPayslips();
      }
    } catch (error) {
      console.error("Error deleting payslip:", error);
      toast.error("Failed to delete payslip");
    }
  };

  const handleSyncXero = async () => {
    setSyncingXero(true);
    try {
      const endpoint = isViewingOther 
        ? `${API}/staff/xero/payslips/sync/${userId}`
        : `${API}/staff/xero/payslips/sync`;
      
      const res = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = res.data;
      if (data.synced_count > 0) {
        toast.success(`Synced ${data.synced_count} payslips from Xero`);
        // Refresh payslips list
        if (isViewingOther) {
          fetchStaffPayslips();
        } else {
          fetchPayslips();
        }
      } else if (data.matched_employees === 0) {
        toast.error("No matching employee found in Xero. Check email addresses match.");
      } else {
        toast.info("No new payslips to sync");
      }
    } catch (error) {
      console.error("Error syncing from Xero:", error);
      const message = error.response?.data?.detail || "Failed to sync from Xero";
      toast.error(message);
    } finally {
      setSyncingXero(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const endpoint = isViewingOther 
        ? `${API}/staff/profile/${userId}`
        : `${API}/staff/profile/me`;
      await axios.patch(endpoint, editedProfile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(editedProfile);
      setEditMode(false);
      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);

    try {
      const res = await axios.post(`${API}/staff/profile/photo`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      setProfile(prev => ({ ...prev, photo_url: res.data.photo_url }));
      setEditedProfile(prev => ({ ...prev, photo_url: res.data.photo_url }));
      toast.success("Profile photo updated");
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error("Failed to upload photo");
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current || !passwordData.new || !passwordData.confirm) {
      toast.error("Please fill in all password fields");
      return;
    }
    
    if (passwordData.new !== passwordData.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.new.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.post(`${API}/staff/change-password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Password changed successfully!");
      setShowPasswordModal(false);
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      console.error("Error changing password:", error);
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleHolidayRequest = async () => {
    if (!holidayRequest.start_date || !holidayRequest.end_date) {
      toast.error("Please select start and end dates");
      return;
    }

    setSubmittingHoliday(true);
    try {
      await axios.post(`${API}/staff/holidays/request`, {
        start_date: format(holidayRequest.start_date, "yyyy-MM-dd"),
        end_date: format(holidayRequest.end_date, "yyyy-MM-dd"),
        notes: holidayRequest.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Holiday request submitted");
      setShowHolidayModal(false);
      setHolidayRequest({ start_date: null, end_date: null, notes: "" });
      fetchHolidays();
    } catch (error) {
      console.error("Error submitting holiday:", error);
      toast.error("Failed to submit holiday request");
    } finally {
      setSubmittingHoliday(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "??";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const ytdSalary = profile?.ytd_salary || 0;
  const holidaysRemaining = profile?.holiday_allowance - (profile?.holidays_taken || 0);

  // Only allow editing if viewing own profile OR director viewing another
  const canEdit = !isViewingOther || isDirector;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {isViewingOther && (
                <Button variant="ghost" size="icon" onClick={() => navigate("/staff")}>
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              <h1 className="text-2xl font-bold text-slate-900">
                {isViewingOther ? `${profile?.name}'s Profile` : "My Profile"}
              </h1>
            </div>
            {canEdit && !editMode ? (
              <Button onClick={() => setEditMode(true)} data-testid="edit-profile-btn">
                <Edit className="w-4 h-4 mr-2" />
                Edit Profile
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  setEditMode(false);
                  setEditedProfile(profile);
                }}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSaveProfile} disabled={saving}>
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Profile Header Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              {/* Profile Photo */}
              <div className="relative">
                <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                  <AvatarImage src={profile?.photo_url} />
                  <AvatarFallback className="text-2xl bg-gradient-to-br from-blue-500 to-purple-600 text-white">
                    {getInitials(profile?.name)}
                  </AvatarFallback>
                </Avatar>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white hover:bg-blue-700 transition-colors shadow-lg"
                  data-testid="change-photo-btn"
                >
                  <Camera className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-slate-900">{profile?.name}</h2>
                <p className="text-slate-500">{profile?.title || profile?.role?.replace("_", " ")}</p>
                <div className="flex items-center gap-4 mt-3 text-sm text-slate-600">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {profile?.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    {profile?.branch || "Head Office"}
                  </span>
                </div>
                {!isViewingOther && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => setShowPasswordModal(true)}
                    data-testid="change-password-btn"
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                )}
              </div>

              {/* Quick Stats */}
              <div className="flex gap-4">
                <div className="text-center p-4 bg-emerald-50 rounded-xl">
                  <p className="text-2xl font-bold text-emerald-600">£{ytdSalary.toLocaleString()}</p>
                  <p className="text-xs text-emerald-700">YTD Earnings</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-xl">
                  <p className="text-2xl font-bold text-blue-600">{holidaysRemaining}</p>
                  <p className="text-xs text-blue-700">Days Left</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Private Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-slate-600" />
                Private Information
              </CardTitle>
              <CardDescription>Your personal contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Correspondence Address */}
              <div>
                <Label className="text-xs text-slate-500 uppercase">Correspondence Address</Label>
                {editMode ? (
                  <Textarea
                    value={editedProfile.address || ""}
                    onChange={(e) => setEditedProfile({...editedProfile, address: e.target.value})}
                    placeholder="Enter your address..."
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm mt-1 text-slate-700">{profile?.address || "Not set"}</p>
                )}
              </div>

              <Separator />

              {/* Phone Numbers */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500 uppercase">Mobile</Label>
                  {editMode ? (
                    <Input
                      value={editedProfile.mobile || ""}
                      onChange={(e) => setEditedProfile({...editedProfile, mobile: e.target.value})}
                      placeholder="07xxx xxxxxx"
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-sm mt-1 text-slate-700 flex items-center gap-2">
                      <Phone className="w-4 h-4 text-slate-400" />
                      {profile?.mobile || "Not set"}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-xs text-slate-500 uppercase">Home Phone</Label>
                  {editMode ? (
                    <Input
                      value={editedProfile.home_phone || ""}
                      onChange={(e) => setEditedProfile({...editedProfile, home_phone: e.target.value})}
                      placeholder="01234 567890"
                      className="mt-1"
                    />
                  ) : (
                    <p className="text-sm mt-1 text-slate-700 flex items-center gap-2">
                      <Home className="w-4 h-4 text-slate-400" />
                      {profile?.home_phone || "Not set"}
                    </p>
                  )}
                </div>
              </div>

              <Separator />

              {/* Emergency Contact */}
              <div>
                <Label className="text-xs text-slate-500 uppercase flex items-center gap-1">
                  <Heart className="w-3 h-3" />
                  Emergency Contact
                </Label>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div>
                    {editMode ? (
                      <Input
                        value={editedProfile.emergency_name || ""}
                        onChange={(e) => setEditedProfile({...editedProfile, emergency_name: e.target.value})}
                        placeholder="Contact name"
                      />
                    ) : (
                      <p className="text-sm text-slate-700">{profile?.emergency_name || "Not set"}</p>
                    )}
                  </div>
                  <div>
                    {editMode ? (
                      <Input
                        value={editedProfile.emergency_phone || ""}
                        onChange={(e) => setEditedProfile({...editedProfile, emergency_phone: e.target.value})}
                        placeholder="Contact number"
                      />
                    ) : (
                      <p className="text-sm text-slate-700">{profile?.emergency_phone || "Not set"}</p>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Personal Email */}
              <div>
                <Label className="text-xs text-slate-500 uppercase">Personal Email</Label>
                {editMode ? (
                  <Input
                    type="email"
                    value={editedProfile.personal_email || ""}
                    onChange={(e) => setEditedProfile({...editedProfile, personal_email: e.target.value})}
                    placeholder="personal@email.com"
                    className="mt-1"
                  />
                ) : (
                  <p className="text-sm mt-1 text-slate-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {profile?.personal_email || "Not set"}
                  </p>
                )}
              </div>

              <Separator />

              {/* NI Number */}
              <div>
                <Label className="text-xs text-slate-500 uppercase">National Insurance Number</Label>
                {editMode && isDirector ? (
                  <Input
                    value={editedProfile.ni_number || ""}
                    onChange={(e) => setEditedProfile({...editedProfile, ni_number: e.target.value.toUpperCase()})}
                    placeholder="AB123456C"
                    className="mt-1 font-mono"
                    maxLength={9}
                  />
                ) : (
                  <p className="text-sm mt-1 text-slate-700 flex items-center gap-2 font-mono">
                    <Shield className="w-4 h-4 text-slate-400" />
                    {profile?.ni_number || "Not set"}
                  </p>
                )}
              </div>

              <Separator />

              {/* Bank Details */}
              <div>
                <p className="text-xs text-slate-500 uppercase mb-2">Bank Details</p>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-slate-400">Account Name</Label>
                    {editMode && isDirector ? (
                      <Input
                        value={editedProfile.bank_account_name || ""}
                        onChange={(e) => setEditedProfile({...editedProfile, bank_account_name: e.target.value})}
                        placeholder="Account holder name"
                        className="mt-1"
                      />
                    ) : (
                      <p className="text-sm text-slate-700">{profile?.bank_account_name || "Not set"}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-slate-400">Sort Code</Label>
                      {editMode && isDirector ? (
                        <Input
                          value={editedProfile.bank_sort_code || ""}
                          onChange={(e) => setEditedProfile({...editedProfile, bank_sort_code: e.target.value})}
                          placeholder="00-00-00"
                          className="mt-1 font-mono"
                          maxLength={8}
                        />
                      ) : (
                        <p className="text-sm text-slate-700 font-mono">{profile?.bank_sort_code || "Not set"}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Account Number</Label>
                      {editMode && isDirector ? (
                        <Input
                          value={editedProfile.bank_account_number || ""}
                          onChange={(e) => setEditedProfile({...editedProfile, bank_account_number: e.target.value})}
                          placeholder="12345678"
                          className="mt-1 font-mono"
                          maxLength={8}
                        />
                      ) : (
                        <p className="text-sm text-slate-700 font-mono">
                          {profile?.bank_account_number ? `****${profile.bank_account_number.slice(-4)}` : "Not set"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Salary & Commission */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="w-5 h-5 text-emerald-600" />
                Salary & Commission
              </CardTitle>
              <CardDescription>Your current pay structure</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase">Basic Salary</p>
                  <p className="text-xl font-bold text-slate-900">
                    £{(profile?.basic_salary || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-500">per annum</p>
                </div>
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-600 uppercase">YTD Earnings</p>
                  <p className="text-xl font-bold text-emerald-700">
                    £{ytdSalary.toLocaleString()}
                  </p>
                  <p className="text-xs text-emerald-600">gross this year</p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs text-slate-500 uppercase mb-2">Commission Structure</p>
                {profile?.commission_structure ? (
                  <div className="space-y-2">
                    {profile.commission_structure.type === 'sales_processor' && (
                      <>
                        <div className="flex justify-between p-2 bg-blue-50 rounded">
                          <span className="text-sm">Exchange ≤ £50k</span>
                          <Badge className="bg-blue-100 text-blue-700">{profile.commission_structure.exchange_value_up_to_50k_percent}%</Badge>
                        </div>
                        <div className="flex justify-between p-2 bg-blue-50 rounded">
                          <span className="text-sm">Exchange &gt; £50k</span>
                          <Badge className="bg-blue-100 text-blue-700">{profile.commission_structure.exchange_value_above_50k_percent}%</Badge>
                        </div>
                        {profile.commission_structure.description && (
                          <p className="text-xs text-slate-500 mt-2">{profile.commission_structure.description}</p>
                        )}
                      </>
                    )}
                    {profile.commission_structure.type === 'sales' && (
                      <>
                        <div className="flex justify-between p-2 bg-green-50 rounded">
                          <span className="text-sm">Base Commission</span>
                          <Badge className="bg-green-100 text-green-700">{profile.commission_structure.base_rate || 7}%</Badge>
                        </div>
                        <div className="flex justify-between p-2 bg-green-50 rounded">
                          <span className="text-sm">Share of Pot</span>
                          <Badge variant="outline">{profile.commission_structure.pot_share || 'Equal Split'}</Badge>
                        </div>
                      </>
                    )}
                    {profile.commission_structure.type === 'lettings' && (
                      <>
                        {profile.commission_structure.re_let && (
                          <div className="flex justify-between p-2 bg-purple-50 rounded">
                            <span className="text-sm">Re-let</span>
                            <Badge className="bg-purple-100 text-purple-700">£{profile.commission_structure.re_let}</Badge>
                          </div>
                        )}
                        {profile.commission_structure.new_fully_managed_8_or_less && (
                          <div className="flex justify-between p-2 bg-purple-50 rounded">
                            <span className="text-sm">New FM (≤8%)</span>
                            <Badge className="bg-purple-100 text-purple-700">£{profile.commission_structure.new_fully_managed_8_or_less}</Badge>
                          </div>
                        )}
                        {profile.commission_structure.new_fully_managed_10_plus && (
                          <div className="flex justify-between p-2 bg-purple-50 rounded">
                            <span className="text-sm">New FM (10%+)</span>
                            <Badge className="bg-purple-100 text-purple-700">£{profile.commission_structure.new_fully_managed_10_plus}</Badge>
                          </div>
                        )}
                        {profile.commission_structure.find_tenant_only && (
                          <div className="flex justify-between p-2 bg-purple-50 rounded">
                            <span className="text-sm">Find Tenant Only</span>
                            <Badge className="bg-purple-100 text-purple-700">£{profile.commission_structure.find_tenant_only}</Badge>
                          </div>
                        )}
                        {profile.commission_structure.move_ins && (
                          <div className="flex justify-between p-2 bg-purple-50 rounded">
                            <span className="text-sm">Move-ins</span>
                            <Badge className="bg-purple-100 text-purple-700">£{profile.commission_structure.move_ins}</Badge>
                          </div>
                        )}
                        {profile.commission_structure.google_review && (
                          <div className="flex justify-between p-2 bg-amber-50 rounded">
                            <span className="text-sm">Google Review</span>
                            <Badge className="bg-amber-100 text-amber-700">£{profile.commission_structure.google_review}</Badge>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-slate-500 italic">No commission structure assigned</p>
                    {profile?.commission_eligible && (
                      <Badge className="bg-green-100 text-green-700">Commission Eligible</Badge>
                    )}
                  </div>
                )}
              </div>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate("/commission")}
              >
                View Full Commission Details
              </Button>
            </CardContent>
          </Card>

          {/* Holiday Planner */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Palmtree className="w-5 h-5 text-blue-600" />
                    Holiday Planner
                  </CardTitle>
                  <CardDescription>Your annual leave allowance</CardDescription>
                </div>
                <Button onClick={() => setShowHolidayModal(true)} data-testid="request-holiday-btn">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Request Holiday
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Holiday Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{profile?.holiday_allowance || 28}</p>
                  <p className="text-xs text-blue-700">Total Days</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{profile?.holidays_taken || 0}</p>
                  <p className="text-xs text-amber-700">Days Taken</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{holidaysRemaining}</p>
                  <p className="text-xs text-green-700">Remaining</p>
                </div>
              </div>

              {/* Recent/Upcoming Holidays */}
              <div>
                <p className="text-xs text-slate-500 uppercase mb-2">Recent & Upcoming</p>
                {holidays.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">No holidays booked</p>
                ) : (
                  <div className="space-y-2">
                    {holidays.slice(0, 5).map((holiday, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">
                            {format(new Date(holiday.start_date), "d MMM")} - {format(new Date(holiday.end_date), "d MMM yyyy")}
                          </span>
                        </div>
                        <Badge className={
                          holiday.status === "approved" ? "bg-green-100 text-green-700" :
                          holiday.status === "pending" ? "bg-amber-100 text-amber-700" :
                          "bg-red-100 text-red-700"
                        }>
                          {holiday.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Historic Payslips */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-purple-600" />
                    Historic Payslips
                  </CardTitle>
                  <CardDescription>
                    {isViewingOther ? "Manage payslips for this staff member" : "Download your previous payslips"}
                  </CardDescription>
                </div>
                {isDirector && isViewingOther && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={handleSyncXero} 
                      disabled={syncingXero}
                      data-testid="sync-xero-btn"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncingXero ? 'animate-spin' : ''}`} />
                      {syncingXero ? "Syncing..." : "Sync from Xero"}
                    </Button>
                    <Button onClick={() => setShowPayslipModal(true)} data-testid="add-payslip-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Add Payslip
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {payslips.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-slate-400 mb-4">No payslips available yet</p>
                  {isDirector && isViewingOther && (
                    <Button variant="outline" onClick={handleSyncXero} disabled={syncingXero}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${syncingXero ? 'animate-spin' : ''}`} />
                      {syncingXero ? "Syncing..." : "Sync from Xero"}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {payslips.map((payslip, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${payslip.source === 'xero' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                          <FileText className={`w-5 h-5 ${payslip.source === 'xero' ? 'text-blue-600' : 'text-purple-600'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-sm">
                            {payslip.month} {payslip.year}
                            {payslip.source === 'xero' && (
                              <Badge variant="outline" className="ml-2 text-xs">Xero</Badge>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">
                            £{payslip.gross_amount?.toLocaleString()} gross • £{payslip.net_amount?.toLocaleString()} net
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" data-testid={`download-payslip-${idx}`}>
                          <Download className="w-4 h-4" />
                        </Button>
                        {isDirector && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeletePayslip(payslip.payslip_id)}
                            data-testid={`delete-payslip-${idx}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Holiday Request Modal */}
      <Dialog open={showHolidayModal} onOpenChange={setShowHolidayModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Holiday</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start mt-1">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {holidayRequest.start_date 
                        ? format(holidayRequest.start_date, "d MMM yyyy")
                        : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={holidayRequest.start_date}
                      onSelect={(date) => setHolidayRequest({...holidayRequest, start_date: date})}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start mt-1">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {holidayRequest.end_date 
                        ? format(holidayRequest.end_date, "d MMM yyyy")
                        : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={holidayRequest.end_date}
                      onSelect={(date) => setHolidayRequest({...holidayRequest, end_date: date})}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={holidayRequest.notes}
                onChange={(e) => setHolidayRequest({...holidayRequest, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHolidayModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleHolidayRequest} disabled={submittingHoliday}>
              {submittingHoliday ? "Submitting..." : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payslip Modal (Directors only) */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payslip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Month</Label>
                <Select
                  value={newPayslip.month}
                  onValueChange={(value) => {
                    const monthNames = ["January", "February", "March", "April", "May", "June", 
                                       "July", "August", "September", "October", "November", "December"];
                    const monthNum = monthNames.indexOf(value) + 1;
                    setNewPayslip({...newPayslip, month: value, month_num: monthNum});
                  }}
                >
                  <SelectTrigger className="mt-1" data-testid="payslip-month-select">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"].map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Select
                  value={newPayslip.year.toString()}
                  onValueChange={(value) => setNewPayslip({...newPayslip, year: parseInt(value)})}
                >
                  <SelectTrigger className="mt-1" data-testid="payslip-year-select">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {[2023, 2024, 2025, 2026].map((y) => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Basic Pay (£)</Label>
                <Input
                  type="number"
                  value={newPayslip.basic_pay}
                  onChange={(e) => setNewPayslip({...newPayslip, basic_pay: e.target.value})}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="payslip-basic-pay"
                />
              </div>
              <div>
                <Label>Commission (£)</Label>
                <Input
                  type="number"
                  value={newPayslip.commission}
                  onChange={(e) => setNewPayslip({...newPayslip, commission: e.target.value})}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="payslip-commission"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bonus (£)</Label>
                <Input
                  type="number"
                  value={newPayslip.bonus}
                  onChange={(e) => setNewPayslip({...newPayslip, bonus: e.target.value})}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="payslip-bonus"
                />
              </div>
              <div>
                <Label>Deductions (£)</Label>
                <Input
                  type="number"
                  value={newPayslip.deductions}
                  onChange={(e) => setNewPayslip({...newPayslip, deductions: e.target.value})}
                  placeholder="0.00"
                  className="mt-1"
                  data-testid="payslip-deductions"
                />
              </div>
            </div>

            {/* Calculated totals */}
            <div className="p-3 bg-slate-50 rounded-lg space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Gross Amount:</span>
                <span className="font-medium">
                  £{(parseFloat(newPayslip.basic_pay || 0) + parseFloat(newPayslip.commission || 0) + parseFloat(newPayslip.bonus || 0)).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Net Amount:</span>
                <span className="font-medium text-green-600">
                  £{(parseFloat(newPayslip.basic_pay || 0) + parseFloat(newPayslip.commission || 0) + parseFloat(newPayslip.bonus || 0) - parseFloat(newPayslip.deductions || 0)).toLocaleString()}
                </span>
              </div>
            </div>

            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={newPayslip.notes}
                onChange={(e) => setNewPayslip({...newPayslip, notes: e.target.value})}
                placeholder="Any additional notes..."
                className="mt-1"
                data-testid="payslip-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPayslip} disabled={addingPayslip} data-testid="submit-payslip-btn">
              {addingPayslip ? "Adding..." : "Add Payslip"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password"
                value={passwordData.current}
                onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                placeholder="Enter current password"
                className="mt-1"
                data-testid="current-password-input"
              />
            </div>
            <Separator />
            <div>
              <Label>New Password</Label>
              <Input
                type="password"
                value={passwordData.new}
                onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                placeholder="Enter new password (min 8 characters)"
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={passwordData.confirm}
                onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                placeholder="Confirm new password"
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>
            {passwordData.new && passwordData.confirm && passwordData.new !== passwordData.confirm && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Passwords do not match
              </p>
            )}
            {passwordData.new && passwordData.new.length < 8 && (
              <p className="text-sm text-amber-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Password must be at least 8 characters
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowPasswordModal(false);
              setPasswordData({ current: "", new: "", confirm: "" });
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleChangePassword} 
              disabled={changingPassword || !passwordData.current || !passwordData.new || passwordData.new !== passwordData.confirm || passwordData.new.length < 8}
              data-testid="submit-password-btn"
            >
              {changingPassword ? "Changing..." : "Change Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffProfile;
